import React, { useEffect, useMemo } from 'react'
import { User } from 'lucide-react'
import apiService from '../services/api'
import { useTranslation } from 'react-i18next'
import { LOGO_SRC } from '../config/brand'

// Временный флаг: false = только Google (большая кнопка), true = все 4 провайдера как раньше.
// Чтобы вернуть Telegram/Steam/Discord — поставь true.
const SHOW_ALL_PROVIDERS = true

const Welcome = () => {
  const { t } = useTranslation()
  const tags = useMemo(() => {
    return [
      { label: t('welcome.tags.players', { count: '1 050 830' }), tone: 'green', icon: 'user' },
      { label: t('welcome.tags.rating'), tone: 'blue' },
      { label: t('welcome.tags.withdrawal'), tone: 'purple' },
    ]
  }, [t])

  const providers = useMemo(() => {
    // Ref для передачи в Telegram-бота: из URL ?ref= или из куки skinoz_auth_ref
    const authRef = apiService.captureAuthRefFromUrl()

    // Кнопка Telegram ведёт напрямую на бота с передачей ref в параметре start
    const telegramBotUrl = new URL('https://t.me/skinozbot')
    if (authRef) {
      telegramBotUrl.searchParams.set('start', authRef)
    }

    const all = [
      {
        id: 'google',
        title: 'Google',
        icon: '/images/welcome-page/google.png',
        tone: 'google',
        primary: true,
        href: apiService.getAuthLoginUrl('google')
      },
      {
        id: 'telegram',
        title: 'Telegram',
        icon: '/images/welcome-page/telegram.png',
        tone: 'telegram',
        primary: false,
        href: telegramBotUrl.toString()
      },
      {
        id: 'steam',
        title: 'Steam',
        icon: '/images/welcome-page/steam.png',
        tone: 'steam',
        href: apiService.getAuthLoginUrl('steam')
      },
      {
        id: 'discord',
        title: 'Discord',
        icon: '/images/welcome-page/discord.png',
        tone: 'discord',
        href: apiService.getAuthLoginUrl('discord')
      }
    ]

    // Временный режим: только Google как primary (большая кнопка).
    if (!SHOW_ALL_PROVIDERS) {
      return all.filter((p) => p.id === 'google').map((p) => ({ ...p, primary: true }))
    }

    return all
  }, [])

  return (
    <div className="min-h-screen lg:h-screen bg-dark-primary text-white overflow-hidden">
      {/*
        Десктоп: 2 колонки 50/50 (слева фон-картинка, справа контент)
        Мобилка: картинка сверху, контент снизу
      */}
      <div className="min-h-screen lg:h-screen grid grid-rows-[260px_1fr] lg:grid-rows-1 lg:grid-cols-2">
        {/* Левая часть: фоновая картинка */}
        <div
          className="relative overflow-hidden"
          style={{
            backgroundImage: 'url(/images/welcome-page/welcome-background.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Плавный переход на стыке с правой секцией (только на десктопе) */}
          <div className="hidden lg:block absolute inset-y-0 right-0 w-64 bg-gradient-to-r from-transparent to-dark-primary pointer-events-none" />
        </div>

        {/* Правая часть: контент */}
        <div className="relative bg-dark-primary">
          {/* Фоновый слой справа (легкие акценты) */}
          <div className="absolute inset-0">
            <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-accent-primary/10 blur-3xl" />
            {/* <div className="absolute -bottom-56 -right-56 w-[640px] h-[640px] rounded-full bg-accent-secondary/10 blur-3xl" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/60" /> */}
          </div>

          <div className="relative z-10 h-full px-6 py-10 flex">
            <div className="w-full max-w-[540px] mx-auto flex flex-col justify-center">
              <div className="text-center">
                <div className="relative inline-block">
                  <img
                    src={LOGO_SRC}
                    alt="SKINOZ"
                    className="relative z-10 mx-auto w-full max-w-[260px] sm:max-w-[320px]"
                  />
                  <div className="pointer-events-none absolute left-1/2 top-full h-8 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/25 blur-2xl" />
                </div>

                {/* Заголовок - скрыт ВРЕМЕННО */}
                <p className="mt-6 text-3xl sm:text-5xl tracking-tight hidden">
                  {t('welcome.heroTitle')}
                </p>
              </div>

              {/* Теги / метрики - скрыты ВРЕМЕННО */}
              <div className="mt-16 flex flex-wrap justify-center gap-2 hidden">
                {tags.map((tag) => {
                  const toneClasses =
                    tag.tone === 'green'
                      ? 'bg-accent-primary/10 border-accent-primary/20 text-accent-primary'
                      : tag.tone === 'blue'
                        ? 'bg-info/10 border-info/20 text-info'
                        : tag.tone === 'purple'
                          ? 'bg-purple-400/10 border-purple-400/20 text-purple-300'
                          : 'bg-white/5 border-white/10 text-text-secondary'

                  return (
                    <div
                      key={tag.label}
                      className={`px-3 py-1.5 rounded-full border text-sm ${toneClasses}`}
                    >
                      {tag.icon === 'user' && <User size={14} className="inline mr-1.5 -mt-0.5" />}
                      {tag.label}
                    </div>
                  )
                })}
              </div>

              {/* Кнопки авторизации */}
              <div className="mt-16 w-full">
                <div className="grid grid-cols-1 gap-3">
                  {providers.filter((provider) => provider.primary).map((provider) => {
                    // Трекинг клика по primary-кнопке авторизации в Яндекс.Метрику
                    const handlePrimaryClick = () => {
                      if (typeof window !== 'undefined' && typeof window.ym === 'function') {
                        window.ym(104258304, 'reachGoal', 'pwa_google_login_click')
                      }
                    }

                    return (
                      <a
                        key={provider.id}
                        href={provider.href}
                        onClick={handlePrimaryClick}
                        className="group block w-full px-5 py-4 rounded-2xl border border-cyan-400/40 bg-gradient-to-r from-cyan-400/20 to-accent-primary/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(34,211,238,0.25)]"
                      >
                        <div className="flex items-center justify-center gap-3">
                          <img
                            src={provider.icon}
                            alt={provider.title}
                            className="w-11 h-11 object-contain shrink-0"
                            loading="lazy"
                          />
                          <div className="text-lg font-bold tracking-tight">
                            {t('welcome.loginWith', { provider: provider.title })}
                          </div>
                        </div>
                      </a>
                    )
                  })}
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {providers.filter((provider) => !provider.primary).map((provider) => {
                    const accentClasses =
                      provider.tone === 'steam'
                        ? 'hover:border-sky-400/40 hover:bg-sky-400/10 hover:shadow-[0_0_24px_rgba(56,189,248,0.2)]'
                        : provider.tone === 'google'
                          ? 'hover:border-red-400/35 hover:bg-red-400/10 hover:shadow-[0_0_24px_rgba(248,113,113,0.18)]'
                          : provider.tone === 'discord'
                            ? 'hover:border-indigo-400/40 hover:bg-indigo-400/12 hover:shadow-[0_0_24px_rgba(129,140,248,0.2)]'
                            : 'hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:shadow-[0_0_24px_rgba(34,211,238,0.18)]'

                    return (
                      <a
                        key={provider.id}
                        href={provider.href}
                        className={`group block w-full px-4 py-3 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.09] to-white/[0.04] transition-all duration-300 hover:-translate-y-0.5 ${accentClasses}`}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={provider.icon}
                            alt={provider.title}
                            className="w-9 h-9 object-contain shrink-0"
                            loading="lazy"
                          />
                          <div className="text-left">
                            <div className="text-sm sm:text-base font-semibold tracking-tight">
                              {provider.title}
                            </div>
                          </div>
                        </div>
                      </a>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Welcome
